import { z } from "zod";

export const RecommendationSchema = z.object({
  id: z.string().min(1).max(200), semanticKey: z.string().min(1).max(1200),
  scope: z.string().min(1).max(900), intent: z.string().min(1).max(100),
  need: z.string().max(160).default(""), basisVersion: z.string().max(200), text: z.string().min(2).max(180),
});
export type Recommendation = z.infer<typeof RecommendationSchema>;
export const RECOMMENDATION_HISTORY_SCOPES = 128;
export const RECOMMENDATION_HISTORY_PER_SCOPE = 100;
/** Persistence is bounded per object/constraint scope, not by one global list
 * of 100 questions that discards old objects after visiting 101 new pages. */
export function boundRecommendationSelections(items: Recommendation[]): Recommendation[] {
  const scopes = new Map<string, Recommendation[]>();
  for (const item of items) {
    const previous = scopes.get(item.scope) || [];
    const next = [...previous.filter(old => old.id !== item.id), item].slice(-RECOMMENDATION_HISTORY_PER_SCOPE);
    scopes.delete(item.scope); scopes.set(item.scope, next);
    while (scopes.size > RECOMMENDATION_HISTORY_SCOPES) scopes.delete(scopes.keys().next().value!);
  }
  return [...scopes.values()].flat();
}
export const RecommendationContextSchema = z.object({
  version: z.number().int().nonnegative(), selectedVersion: z.number().int().nonnegative(),
  signature: z.string().max(3000), scope: z.string().max(900), profileVersion: z.string().max(100),
  corrections: z.array(z.string().max(1000)).max(12),
  verified: z.array(z.string().max(1000)).max(12).default([]),
  selected: z.array(RecommendationSchema).max(100).default([]),
  answered: z.array(z.string().max(200)).max(100).default([]),
  shown: z.array(RecommendationSchema).max(20).default([]),
});
export const RecommendationResponseSchema = z.object({
  questions: z.array(z.string()), recommendations: z.array(RecommendationSchema).max(3),
  protocolVersion: z.literal(2), basisVersion: z.string(), promptVersion: z.string(),
});
export type RecommendationResponse = z.infer<typeof RecommendationResponseSchema>;
export const recommendationText = (text: string) => text.normalize("NFKC").toLowerCase().replace(/[\p{P}\p{Z}\s]/gu, "");
const intents: [string, RegExp][] = [
  ["supplier-trust", /供应商.*(可靠|可信|资质)|商家.*(可信|可靠)|核实.*商家|supplier.*(trust|reliab)/i],
  ["risk", /风险|避坑|注意事项|注意什么|合规|违规|risk|compliance/i],
  ["certification", /认证|资质|证书|certificat/i], ["profit", /利润|赚|毛利|profit|margin/i],
  ["cost", /成本|费用|运费|cost|fee/i], ["next", /下一步|接下来|怎么继续|next step/i],
  ["purpose", /主要做什么|什么用|功能|干什么|用途/], ["fix", /报错|错误|失败|解决|error|fix/i],
  ["optimize", /优化|改进|改善|improve|optimi/i], ["price", /定价|售价|卖多少钱|pricing/i],
];
export function recommendationIntent(text: string) { return intents.find(([, re]) => re.test(text))?.[0] || recommendationText(text); }
export function recommendationNeed(text: string) { return (text.match(/\b(?:UKCA|CE|FCC|FDA|CPC|REACH|RoHS)\b/gi) || []).map(v => v.toLowerCase()).sort().join(","); }
export function recommendationHash(value: string | undefined) { let h = 2166136261; for (const c of value || "") { h ^= c.charCodeAt(0); h = Math.imul(h, 16777619); } return (h >>> 0).toString(36); }
export function makeRecommendation(text: string, scope: string, basisVersion: string, intent = recommendationIntent(text), need = recommendationNeed(text)): Recommendation {
  const semanticKey = [intent, scope, need].join("|");
  return { id: `rec_${recommendationHash(semanticKey)}`, semanticKey, scope, intent, need, basisVersion, text };
}
export function dedupeRecommendations(items: Recommendation[], excluded: Recommendation[] = []): Recommendation[] {
  const seen = [...excluded]; const result: Recommendation[] = [];
  for (const item of items) {
    if (seen.some(old => old.scope === item.scope && (old.id === item.id || old.semanticKey === item.semanticKey || recommendationText(old.text) === recommendationText(item.text)
      || (recommendationIntent(old.text) === recommendationIntent(item.text) && old.need === item.need)))) continue;
    seen.push(item); result.push(item);
  }
  return result;
}
